import React from "react";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import FilterListView from "../../components/FilterListView";
import Skeleton from "@material-ui/lab/Skeleton";

import Scenario from "../../api/Scenario";
import Header from "../../components/utils/Header";
import { formatRatio, formatRatioPercent, formatVolume } from "../../utils";
import ConditionalFormat from "../../components/utils/ConditionalFormat";
import ScenarioSelect from "../scenarios/ScenarioSelect";
import BaseScenarioDashboardView from "../BaseScenarioDashboardView";
import DataFrameDownload from "../../components/utils/DataFrameDownload";
import DataFrame from "../../components/utils/DataFrame";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import InfoIcon from "@material-ui/icons/Info";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";

const styles = (theme) => ({
  tablePaper: {
    padding: theme.spacing(2),
    overflow: "auto",
  },
  tableSeparator: {
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
  titlePadding: {
    padding: theme.spacing(2),
  },
  titleTable: {
    color: "#333",
  },
  tooltipIcon: {
    marginTop: "-5px",
  },
  accordianDetail: {
    flexDirection: "column",
    gap: "1.5rem",
  },
});

@withStyles(styles)
@observer
class FgSfgSupplyView extends BaseScenarioDashboardView {
  static apiSubPath = "/fg_sfg_supply/";

  @observable scenario1 = null;
  @observable scenario2 = null;
  @observable groupFactory = false;
  @observable groupTechnology = true;
  @observable goodsLevel = null;
  @observable dateLevel = null;
  @observable unit = null;

  _setData(data) {
    this.scenario1 = data.scenario1;
    this.scenario2 = data.scenario2;
    this.groupFactory = data.group_factory;
    this.groupTechnology = data.group_technology;
    this.goodsLevel = data.goods_level;
    this.dateLevel = data.date_level;
    this.unit = data.unit;
  }

  _getQueryParams() {
    return {
      scenario1: this.scenario1,
      scenario2: this.scenario2,
      group_factory: this.groupFactory ? 1 : 0,
      group_technology: this.groupTechnology ? 1 : 0,
      goods_level: this.goodsLevel,
      date_level: this.dateLevel,
      unit: this.isFG? this.unit : 'TO',
    };
  }

  @action onGroupFactoryChange = (event) => {
    this.groupFactory = event.target.checked;
    this.updateData();
  };

  @action onGroupTechnologyChange = (event) => {
    this.groupTechnology = event.target.checked;
    this.updateData();
  };

  @action onGoodsLevelChange = (event) => {
    this.goodsLevel = event.target.checked ? "FG" : "SFG";
    this.updateData();
  };

  @action onDateLevelChange = (event) => {
    this.dateLevel = event.target.checked ? "month" : "quarter";
    this.updateData();
  };

  onScenario1Change = this.genericChangeHandler("scenario1");
  onScenario2Change = this.genericChangeHandler("scenario2");
  onUnitChange = this.genericChangeHandler('unit');

  get verboseUnit() {
    if (this.unit === 'TO') {
      return 't';
    }
    if (this.unit === 'KPC') {
      return 'k';
    }
    return '';
  }

  get isFG() {
    return Boolean(this.goodsLevel === 'FG')
  }

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={650} height={55} />;
    }
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          <Typography noWrap>
            Factory
            <Switch
              checked={this.groupFactory}
              onChange={this.onGroupFactoryChange}
            />
          </Typography>
        </Grid>
        <Grid item>
          <Typography noWrap>
            Technology
            <Switch
              checked={this.groupTechnology}
              onChange={this.onGroupTechnologyChange}
            />
          </Typography>
        </Grid>
        <Grid item>
          <Typography noWrap>
            SFG
            <Switch
              checked={this.isFG}
              onChange={this.onGoodsLevelChange}
            />
            FG
          </Typography>
        </Grid>
        <Grid item>
          <Typography noWrap>
            Quarterly
            <Switch
              checked={this.dateLevel === "month"}
              onChange={this.onDateLevelChange}
            />
            Monthly
          </Typography>
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario 1"
            scenarioId={this.scenario1}
            choices={this.data.scenario1_choices}
            onScenarioChange={this.onScenario1Change}
          />
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario 2"
            scenarioId={this.scenario2}
            choices={this.data.scenario2_choices}
            onScenarioChange={this.onScenario2Change}
          />
        </Grid>
        {this.isFG &&
          <Grid item>
            {this.renderChoicesField(
              'Unit', this.data.unit_choices, this.unit, this.onUnitChange,
            )}
          </Grid>
         }
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl(
              "/fg_sfg_supply/?" + this.getQueryString()
            )}
            permission="athena.download_fg_sfg_supply"
          />
        </Grid>
      </Grid>
    );
  }

  formatValue = (tableTitle, column, value, ratio = null, compare = false) => {
    if (value === null) {
      return "—";
    }
    if (
      tableTitle === "index" ||
      (tableTitle === "Below Min Stocks" && column !== "Delta %") ||
      (tableTitle === "Above Max Stocks" && column !== "Delta %")
    ) {
      return value;
    }
    if (
      tableTitle === "Sales Coverage" ||
      tableTitle === "Capacity utilization" ||
      tableTitle === "Production VS Demand" ||
      column === "Delta %"
    ) {
      return compare ? `${formatRatio(value)} pp` : formatRatioPercent(value);
    }
    return compare
      ? `${formatVolume(value, this.verboseUnit, 0)} (${formatRatioPercent(ratio)})`
      : formatVolume(value, this.verboseUnit, 0);
  };

  getConditionalFormatName = (tableTitle, compare) => {
    if (
      compare &&
      (tableTitle === "Below Min Stocks" || tableTitle === "Above Max Stocks")
    ) {
      return "fg-sfg-supply-stock-alerts-comparison";
    }
    if (tableTitle === "Sales Coverage") {
      return compare
        ? "fg-sfg-supply-sales-coverage-comparison"
        : "fg-sfg-supply-sales-coverage";
    } else if (tableTitle === "Capacity utilization") {
      return compare
        ? "fg-sfg-supply-capacity-utilization-comparison"
        : "fg-sfg-supply-capacity-utilization";
    } else if (tableTitle === "Production VS Demand") {
      return compare ? "prod-vs-demand-comparison" : "prod-vs-demand";
    }
    return compare
      ? "fg-sfg-supply-default-comparison"
      : "fg-sfg-supply-default";
  };

  getColumnProps = (
    serializedDataFrame,
    columnsRowIndex,
    columnName,
    columnIndex
  ) => ({
    align: columnsRowIndex === 0 ? "center" : "right",
    leftBorder:
      columnIndex ===
      serializedDataFrame.columns
        .map((columnColumn) => columnColumn[0])
        .indexOf("Scenario 2 - 1"),
  });

  getDataProps = (serializedDataFrame, columnIndex, rowIndex, value) => ({
    align: "right",
    leftBorder:
      columnIndex ===
      serializedDataFrame.columns
        .map((columnColumn) => columnColumn[0])
        .indexOf("Scenario 2 - 1"),
  });

  percentLabelFormatter = (value) => {
    return value + "%";
  };

  unitLabelFormatter = (value) => {
    return value + this.verboseUnit;
  };

  labelFormatter = (value) => {
    return value;
  };

  renderNewTable(
    title,
    titleDescription,
    serializedDataFrame,
    columnGraphData,
    columnUnit
  ) {
    const { classes } = this.props;

    return (
      <Accordion key={title}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            {title}{" "}
            <Tooltip title={titleDescription}>
              <IconButton>
                <InfoIcon className={classes.tooltipIcon} />
              </IconButton>
            </Tooltip>
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordianDetail}>
          <ResponsiveContainer width="95%" height={400}>
            <BarChart
              width={400}
              height={200}
              data={columnGraphData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke="#cdcdcd" />
              <YAxis
                orientation="left"
                domain={["auto", "auto"]}
                stroke="#cdcdcd"
              />
              <RechartsTooltip
                formatter={
                  [
                    "Sales Coverage",
                    "Capacity Utizilation",
                    "Production VS Demand",
                  ].includes(title)
                    ? this.percentLabelFormatter
                    : ["Above Max Stocks", "Below Min Stocks"].includes(title)
                    ? this.labelFormatter
                    : this.unitLabelFormatter
                }
              />
              <Legend />
              <Bar dataKey="Scenario 1" fill="#8884d8" />
              <Bar dataKey="Scenario 2" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
          <DataFrame
            serializedDataFrame={serializedDataFrame}
            formatData={(serializedDataFrame, columnIndex, rowIndex, value) => {
              const { columns } = serializedDataFrame;
              const column = columns[columnIndex][0];
              if (column === "Quarter-Year" || column === "Month-Year") {
                return <strong>{value}</strong>;
              } else {
                return (
                  <ConditionalFormat
                    value={value}
                    name={this.getConditionalFormatName(
                      title,
                      title.includes("Delta")
                    )}
                  >
                    {this.formatValue(title, column, value)}
                  </ConditionalFormat>
                );
              }
            }}
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  renderTables() {
    if (!this.ready) {
      return (
        <Grid item>
          <Skeleton variant="rect" height={450} />
        </Grid>
      );
    }
    return (
      <Grid item>
        {[
          [
            "Sales Coverage",
            "Ratio of supply constrained sales over non constrained demand weighted by value",
            this.data.availability_dataframe,
            this.data.graph_data.availability_graph,
          ],
          [
            "Demand",
            "Demand in unit",
            this.data.demand_dataframe,
            this.data.graph_data.demand_graph,
          ],
          [
            "Production",
            "Production in unit",
            this.data.production_dataframe,
            this.data.graph_data.production_graph,
          ],
          [
            "Stocks",
            "Stocks in unit",
            this.data.stocks_dataframe,
            this.data.graph_data.stocks_graph,
          ],
          [
            "Below Min Stocks",
            "Number of products below min stock for 3 months or more during the period",
            this.data.below_min_dataframe,
            this.data.graph_data.below_min_graph,
          ],
          [
            "Above Max Stocks",
            "Number of products above max stock for 3 months or more during the period",
            this.data.above_max_dataframe,
            this.data.graph_data.above_max_graph,
          ],
          [
            "Capacity utilization",
            "Ratio of production over available capacity",
            this.data.capacity_utilization_dataframe,
            this.data.graph_data.capacity_graph,
          ],
          [
            "Production VS Demand",
            "Ratio of production over demand",
            this.data.production_vs_demand_dataframe,
            this.data.graph_data.production_vs_demand_graph,
          ],
        ].map(([title, titleDescription, data, columnGraphData]) =>
          this.renderNewTable(title, titleDescription, data, columnGraphData)
        )}
      </Grid>
    );
  }

  render() {
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={2}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="FG/SFG Supply" />
          </Grid>
          <Grid item>{this.controls}</Grid>
        </Grid>
        {this.renderTables()}
      </Grid>
    );
  }
}

export default FgSfgSupplyView;
